import { z } from "zod";

import { PROPOSAL_INCLUDES_FOR_FIELDS_LITERAL } from "lib/frontend/cm/utils/constants";
import { ProposalTableContext } from "lib/frontend/cm/utils/types";
import { Company, CustomField, FieldType, Prisma, Review, StockPrice } from "prisma/cm/client";

import { FieldDefinitionArgs, UsageModel } from "./fieldDefinition";
import { ReviewExchangeRates } from "../review/types";

export type FieldDisplayContext = "table" | "award-letter";

export interface FieldHydrationContext {
  review?: Review;
  exchangeRates?: ReviewExchangeRates;
  stockPrice?: StockPrice | null;
  targetCurrency?: string;
  company?: Company;
  findProposerBudgetForProposal?: ProposalTableContext["findProposerBudgetForProposal"];
  findDirectApprovalForProposal?: ProposalTableContext["findDirectApprovalForProposal"];
  customFields?: CustomField[];
  displayContext?: FieldDisplayContext;
}

export enum FieldSourceType {
  SYSTEM_FIELD = "SYSTEM_FIELD",
  CUSTOM_FIELD = "CUSTOM_FIELD",
  PAY_POINT = "PAY_POINT",
  PERFORMANCE_REVIEW = "PERFORMANCE_REVIEW",
  PERFORMANCE_QUESTION = "PERFORMANCE_QUESTION",
  GRANT_TYPE = "GRANT_TYPE",
  BENCHMARK = "BENCHMARK",
  WEIGHTED_COMP = "WEIGHTED_COMP",
}

export type DefinitionFieldType = FieldType | "EQUITY" | "BOOLEAN";

export type FieldCondition =
  | "review-bonus-enabled"
  | "review-ote-enabled"
  | "review-performance-ratings-enabled"
  | "review-salary-enabled"
  | "review-not-ote"
  | "review-bonus-target-enabled"
  | "review-promotions-enabled"
  | "review-equity-enabled";

export type Mixin = Partial<
  Omit<
    FieldDefinitionArgs<any>,
    "valueGetter" | "baseModel" | "id" | "fieldType" | "fieldSourceType" | "contextDependencies"
  >
>;

const proposalForFields = Prisma.validator<Prisma.ProposalArgs>()({
  include: PROPOSAL_INCLUDES_FOR_FIELDS_LITERAL,
});

export type FieldProposal = Prisma.ProposalGetPayload<typeof proposalForFields>;
export type FieldUser = FieldProposal["owner"];
export type FieldRange = FieldProposal["range"];
export type FieldJob = NonNullable<FieldRange>["job"] | null;

export type FieldModelType = FieldProposal | FieldUser | FieldRange;

export type FieldFormatterFunc<T extends FieldModelType> = (args: {
  value: any;
  instance: T;
  context: FieldHydrationContext;
}) => string | undefined | null;

export type FieldExportFormatterFunc = (args: {
  value: any;
  instance: FieldModelType;
  context: FieldHydrationContext;
}) => string;

export type FieldValueGetterFunc<T extends FieldModelType> = (
  instance: T,
  context: FieldHydrationContext,
  rawInstance?: FieldModelType,
) => any;

export type FieldValueSetterFunc<T extends FieldModelType> = (args: {
  newValue: any;
  instance: T;
  context: FieldHydrationContext;
}) => boolean;

export type FieldComparatorFunc<T extends FieldModelType> = (
  value1: any,
  value2: any,
  instance1: T,
  instance2: T,
  context: FieldHydrationContext,
) => number;

export type FieldBaseModelMap = {
  [UsageModel.Proposal]: FieldProposal;
  [UsageModel.User]: FieldUser;
  [UsageModel.Range]: FieldRange;
};

export const SystemFieldIdSchema = z.enum([
  "approverName",
  "awardLetterStatus",
  "bonusAward",
  "bonusAwardDollar",
  "bonusAwardPercent",
  "bonusIncreaseComparison",
  "bonusInput",
  "bonusInputMode",
  "bonusTarget",
  "bonusTargetCurrent",
  "bonusTargetCurrentNominal",
  "bonusTargetCurrentPercent",
  "bonusTargetIncrease",
  "bonusTargetIncreaseComparison",
  "bonusTargetNew",
  "bonusTargetNewNominal",
  "bonusTargetNewPercent",
  "bonusTargetSuggested",
  "bonusTargetSuggestedNominal",
  "bonusTargetSuggestedPercent",
  "bonusTargetValue",
  "bonusTargetValueAfter",
  "bonusTargetValueBeforeAfter",
  "owner.city",
  "owner.country",
  "currentJob",
  "currentTitle",
  "department",
  "employeeId",
  "employeeName",
  "employmentType",
  "employmentSubType",
  "equityAfter",
  "equityBandMinMax",
  "equityBefore",
  "equityBeforeAfter",
  "equityCompaRatioAfter",
  "equityCompaRatioBefore",
  "equityCompaRatioBeforeAfter",
  "equityIncreaseComparison",
  "equityProposed",
  "equitySharesTotal",
  "equitySharesVested",
  "equitySharesUnvested",
  "equitySharesVestingInCurrentYear",
  "equitySharesVestingInCurrentYearValueCents",
  "equitySharesVestedValueCents",
  "equitySharesUnvestedValueCents",
  "equitySharesTotalValueCents",
  "gender",
  "grantIncrease",
  "hrSupportProviders",
  "hrSupportRecipients",
  "inviteStatus",
  "jobBeforeAfter",
  "jobCode",
  "jobFamily",
  "jobFamilyGroup",
  "jobLevel",
  "jobLevelAfter",
  "jobTrack",
  "lastGrantDate",
  "lastCycleName",
  "lastCycleDate",
  "lastCycleSalaryChangeNominal",
  "lastCycleSalaryChangePercent",
  "legalName",
  "owner.location",
  "managerName",
  "managerTitle",
  "marketAdjustment",
  "meritAdjustment",
  "newJob",
  "newTitle",
  "notes",
  "oteAfter",
  "oteBefore",
  "oteBeforeAfter",
  "oteIncrease",
  "oteIncreaseComparison",
  "oteIncreaseDollar",
  "oteIncreasePercent",
  "otherAdjustment",
  "payFrequencyBefore",
  "payFrequencyAfter",
  "performanceRating",
  "performanceScore",
  "primaryEmail",
  "promotionAdjustment",
  "promotions",
  "promotionStatus",
  "proposerName",
  "race",
  "raiseInput",
  "rangeCurrencyCode",
  "rangeIncomeFrequency",
  "rangeTagAfter",
  "rangeTagBefore",
  "reviewName",
  "salaryAfter",
  "salaryBandIncomeFrequency",
  "salaryBandMinMax",
  "salaryBefore",
  "salaryBeforeAfter",
  "salaryCompaRatioAfter",
  "salaryCompaRatioBefore",
  "salaryCompaRatioBeforeAfter",
  "salaryCurrencyCode",
  "salaryEffectiveDate",
  "salaryIncomeFrequency",
  "salaryIncrease",
  "salaryIncreaseComparison",
  "salaryIncreaseDollar",
  "salaryIncreasePercent",
  "seniority",
  "startDate",
  "owner.state",
  "status",
  "suggestedBonus",
  "suggestedEquity",
  "suggestedOTEIncrease",
  "suggestedRaise",
  "suggestedSalaryAdjustmentRange",
  "suggestedSalaryAdjustmentRangeHigh",
  "suggestedSalaryAdjustmentRangeLow",
  "tenure",
  "tenureYears",
  "titleBeforeAfter",
  "variableTargetAfter",
  "variableTargetBefore",
  "variableTargetBeforeAfter",
  "zone",
  "rangeFinder",
  "terminated",
  "rangeDisplayName",
  "rangeTitle",
  "lastLogin",
  "benchmarkPercentile",
  "benchmarkPercentileAfter",
  "isPromoted",
  "variableTargetMix",
  "variableTargetMixAfter",
  "variableTargetMixBeforeAfter",
]);

export type SystemFieldId = z.infer<typeof SystemFieldIdSchema>;

export type FieldSettingsPlaceholders = "SALARY_BAND_PLACEHOLDER" | "EQUITY_BAND_PLACEHOLDER";

export type FieldSettingOrderOption = SystemFieldId | FieldSettingsPlaceholders;

export const fakeFieldProposal: FieldProposal = {
  explicitKind: "proposal",
  id: "550e8400-e29b-41d4-a716-446655440000",
  reviewId: 12345,
  proposerId: null,
  proposer: null,
  suggestedRaise: 10000,
  suggestedBonus: 5000,
  raise: 10000,
  raisePercent: 10,
  raiseInputMode: "DOLLAR",
  variableTarget: null,
  adjustVariableTarget: true,
  bonus: {
    actualValue: null,
    isManuallyEdited: false,
  },
  bonusPercent: 15,
  bonusInputMode: "DOLLAR",
  title: "Staff Software Engineer",
  justification: "Exceptional performance and significant contributions to key projects",
  notes: [],
  cartaEquityGrants: {},
  suggestedRaiseNote: "Based on market analysis and performance",
  suggestedBonusNote: null,
  awardLetterStatus: "PENDING",
  suppressAwardLetter: false,
  awardLetterPdf: null,
  suggestedRaiseInputMode: "DOLLAR",
  suggestedRaisePercent: 12,
  ownerId: "user-123-456-789",
  eligibleForEquity: true,
  eligibleForPromotion: true,
  suggestedBonusInputMode: "DOLLAR",
  suggestedBonusPercent: 8,
  oteIncrease: null,
  oteIncreaseInputMode: "DOLLAR",
  oteIncreasePercent: null,
  companyId: 987,
  rangeId: null,
  managerId: "manager-456-789",
  proposerBudgetId: "DUMMY_PROPOSER_BUDGET_ID",
  adjustBonusTarget: true,
  bonusTargetPercent: 10.5,
  groupAchievementRating: 4.2,
  individualAchievementRating: 4.5,
  bonusTargetNominal: null,
  bonusTargetInputMode: "DOLLAR",
  suggestedBonusTarget: null,
  suggestedBonusTargetNominal: null,
  suggestedBonusTargetPercent: null,
  eligibleForBonus: true,
  eligibleForSalary: true,
  payComponentRatios: {},
  lockPayComponentRatios: true,
  customFieldValues: {},
  logicSuggestedValues: {},
  awardLetterSentAt: null,
  grants: {},
  outlierData: {},
  preventAutoAccept: {},
  awardLetterAcknowledgedAt: null,
  salaryIncomeFrequency: "YEARLY",
  coachingGuidance: null,
  createdAt: new Date("2024-01-01T00:00:00Z"),
  updatedAt: new Date("2024-01-01T00:00:00Z"),
  preRoundedValues: {},
  owner: {
    explicitKind: "user",
    id: "user-123-456-789", // This matches the ownerId from the proposal
    googleId: null,
    companyId: 987, // This matches the companyId from the proposal
    performanceRatings: [] as FieldProposal["owner"]["performanceRatings"],
    proposals: [] as FieldProposal["owner"]["proposals"],
    manager: null as FieldProposal["owner"]["manager"],
    versionHistory: [] as FieldProposal["owner"]["versionHistory"],
    objectives: [] as FieldProposal["owner"]["objectives"],
    hrSupportProviders: [] as FieldProposal["owner"]["hrSupportProviders"],
    hrSupportRecipients: [] as FieldProposal["owner"]["hrSupportRecipients"],
    securePassword: "PlaceholderWhichWillNotAllowAnyAuth",
    name: "Bilbo Baggins",
    role: "EMPLOYEE",
    superUserRole: null,
    governedPartnerId: null,
    isNonEmployee: false,
    worksForComprehensive: false,
    lastLogin: null,
    lastActive: null,
    avatarUrl: null,
    intercomSessionHash: null,
    integrationUserId: null,
    paginationPageSize: 20,
    uiCurrency: "USD",
    privacyPolicyConsentDate: null,
    legalName: "Sarah J. Chen",
    managerId: "manager-456-789", // This matches the managerId from the proposal
    title: "Senior Software Engineer",
    department: "Engineering",
    employmentType: "FULL_TIME",
    employmentSubType: "REGULAR",
    startDate: "2021-03-15",
    endDate: null,
    salary: 1500000,
    salaryEffectiveDate: "2024-01-01",
    salaryIncomeFrequency: "YEARLY",
    salaryCurrencyCode: "USD",
    shares: 5000,
    zone: "PST",
    industry: "Technology",
    bonusTargetPercent: 10.5, // This matches the proposal's bonusTargetPercent
    variableTarget: 15000,
    performanceRating: "Exceeds Expectations",
    priorPerformanceRating: "Meets All Expectations",
    salaryNote: "Last adjusted during 2024 annual review cycle",
    race: null,
    gender: null,
    employeeId: "EMP-2021-0123",
    city: "San Francisco",
    country: "USA",
    state: "CA",
    rangeId: null,
    isTerminated: false,
    extra: {},
    fieldSources: {},
    oktaId: null,
    versionBaseRecordId: null,
    bonusTargetNominal: 15750,
    bonusTargetInputMode: "PERCENT",
    snapshotCycleId: "NOT_SNAPSHOT",
    customFieldValues: {},
    enableCycleEventEmail: true,
    range: null,
    grants: [],
    emails: [],
    performanceAnswers: [],
    createdAt: new Date("2024-01-01T00:00:00Z"),
    updatedAt: new Date("2024-01-01T00:00:00Z"),
    benchmark: {
      data: {
        10: 1000000,
        25: 1200000,
        50: 1530000,
        75: 1800000,
        90: 2000000,
      },
      workflowId: "wf-123",
      levelCode: "L4",
      subFamilyCode: "SF123",
      geo: "US-CA",
      levelLabel: "Senior Engineer",
      subFamilyLabel: "Software Engineering",
      geoLabel: "California, United States",
      titleAtBenchmarkTime: "Senior Software Engineer",
      createdAt: "2024-01-15T08:00:00Z",
    },
    computedCompEventValues: {
      previousSalaryIncreaseNominal: 100000,
      previousSalaryIncreasePercent: 10,
      salaryEffectiveDate: "2024-01-01",
    },
    computedEquityValues: {
      vestingDates: [],
    },
    computedReviewValues: {},
    weightedCompValues: {},
    createdViaEcs: false,
  },
  range: null,
};
